import {
  addUserAddress,
  deleteUserAddress,
  editUser,
  getUser,
  getUserAddresses,
  getUserOrders,
  UserInfo,
} from "../../services/userService";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type UserOrder = {
  id: string;
  orderNumber: string;
  userId: string;
  status: string;
  items: {
    TVATaux: number;
    designation: string;
    imageUrl: string;
    productCode: string;
    quantity: number;
    reference: string;
    unitPriceHT: number;
  }[];
  totalHT: number;
  createdAt: string;
  updatedAt: string;
};

export type UserAddress = {
  id: string;
  fullName: string;
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
};

interface ProductState {
  user: UserInfo | undefined;
  addresses: UserAddress[];
  orders: UserOrder[];
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  user: undefined,
  addresses: [],
  orders: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get User
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action: PayloadAction<UserInfo>) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Edit User
      .addCase(editUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editUser.fulfilled, (state, action: PayloadAction<UserInfo>) => {
        state.loading = false;
        state.user = {
          ...state.user,
          ...action.payload,
        };
      })
      .addCase(editUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get User addresses
      .addCase(getUserAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserAddresses.fulfilled,
        (state, action: PayloadAction<UserAddress[]>) => {
          state.loading = false;
          state.addresses = [...action.payload];
        }
      )
      .addCase(getUserAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Add User address
      .addCase(addUserAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        addUserAddress.fulfilled,
        (state, action: PayloadAction<UserAddress>) => {
          state.loading = false;
          state.addresses = [...state.addresses, action.payload];
        }
      )
      .addCase(addUserAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete User address
      .addCase(deleteUserAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteUserAddress.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          const newAddressList = state.addresses.filter(
            (address) => address.id !== action.payload
          );
          state.addresses = [...newAddressList];
        }
      )
      .addCase(deleteUserAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Orders
      .addCase(getUserOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserOrders.fulfilled,
        (state, action: PayloadAction<UserOrder[]>) => {
          state.loading = false;
          state.orders = action.payload;
        }
      )
      .addCase(getUserOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default userSlice.reducer;
